import { useEffect, useRef, useState } from 'react';
import { PairScreen } from './PairScreen';
import { ViewerScreen } from './ViewerScreen';
import { TvCastSession, type SessionSnapshot } from './pairing/session';
import type { PhoneToTvMessage } from './webrtc/messages';
import { CHARACTER_MODEL, type CameraAngle } from './config/exercise3d';

interface ViewerState {
  modelName: string;
  exerciseName: string | null;
  setIndex: number | null;
  isPaused: boolean;
  playbackSpeed: number;
  cameraAngle: CameraAngle;
  rotationAngle: number | undefined;
  phase: 'countdown' | 'active' | 'rest' | 'complete' | null;
  timerSeconds: number | null;
}

const INITIAL_VIEWER: ViewerState = {
  modelName: CHARACTER_MODEL,
  exerciseName: null,
  setIndex: null,
  isPaused: false,
  playbackSpeed: 1,
  cameraAngle: 'front',
  rotationAngle: undefined,
  phase: null,
  timerSeconds: null,
};

export default function App() {
  const [session, setSession] = useState<SessionSnapshot>({
    state: 'initializing',
    deviceId: null,
    code: null,
    label: null,
    errorMessage: null,
  });
  const [viewer, setViewer] = useState<ViewerState>(INITIAL_VIEWER);
  const sessionRef = useRef<TvCastSession | null>(null);

  useEffect(() => {
    const s = new TvCastSession({
      onSnapshot: (snap) => setSession(snap),
      onCommand: (msg) => applyCommand(msg, setViewer, s),
    });
    sessionRef.current = s;
    void s.start();
    return () => s.stop();
  }, []);

  if (session.state === 'connected') {
    return (
      <ViewerScreen
        modelName={viewer.modelName}
        exerciseName={viewer.exerciseName}
        setIndex={viewer.setIndex}
        isPaused={viewer.isPaused}
        playbackSpeed={viewer.playbackSpeed}
        cameraAngle={viewer.cameraAngle}
        rotationAngle={viewer.rotationAngle}
        phase={viewer.phase}
        timerSeconds={viewer.timerSeconds}
        disconnected={false}
      />
    );
  }

  if (session.state === 'reconnecting') {
    return (
      <ViewerScreen
        modelName={viewer.modelName}
        exerciseName={viewer.exerciseName}
        setIndex={viewer.setIndex}
        isPaused
        playbackSpeed={viewer.playbackSpeed}
        cameraAngle={viewer.cameraAngle}
        rotationAngle={viewer.rotationAngle}
        phase={viewer.phase}
        timerSeconds={viewer.timerSeconds}
        disconnected
      />
    );
  }

  return (
    <PairScreen
      deviceId={session.deviceId}
      code={session.code ?? '······'}
      label={session.label ?? 'TV'}
      state={session.state}
      errorMessage={session.errorMessage}
    />
  );
}

function applyCommand(
  msg: PhoneToTvMessage,
  setViewer: React.Dispatch<React.SetStateAction<ViewerState>>,
  session: TvCastSession,
): void {
  switch (msg.type) {
    case 'SET_EXERCISE':
      setViewer((v) => ({
        ...v,
        modelName: msg.modelName,
        exerciseName: msg.exerciseName ?? v.exerciseName,
        setIndex: msg.setIndex ?? v.setIndex,
      }));
      break;
    case 'SET_PLAYBACK':
      setViewer((v) => ({ ...v, isPaused: msg.paused, playbackSpeed: msg.speed }));
      break;
    case 'SET_ROTATION':
      setViewer((v) => ({ ...v, rotationAngle: msg.angle }));
      break;
    case 'SET_CAMERA_ANGLE':
      setViewer((v) => ({ ...v, cameraAngle: msg.angle, rotationAngle: undefined }));
      break;
    case 'SET_PHASE':
      setViewer((v) => ({
        ...v,
        phase: msg.phase,
        timerSeconds: msg.timerSeconds ?? null,
      }));
      break;
    case 'HEARTBEAT':
      session.send({ type: 'HEARTBEAT_ACK', ts: msg.ts });
      break;
  }
}
